// @flow

//
// React
//
import React, { useState } from 'react';

//
// Material UI
//
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import Modal from '@mui/material/Modal';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import Image from 'mui-image';

import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/AddCircle";

//
// Formatter
//
import {
    fmtPercent,
    fmtMoney, 
} from '../../../../../lib/trix-web-data-commons';

//
// Redux Fantasy
//
import {
    useLocalSelector,
    useDispatchLocal,
} from '../../../../../lib/redux-fantasy-reducers';
import {
    getActionFactory,
	getSelector,
} from '../../../../../lib/trix-web-hal-forms/tools/halFormsRedux';


//
// Component
//
const createRetailStockInventoryAndPriceCellRenderer = (cfg: any): any => (props: any): any => {
    const inProgress = useLocalSelector (
        getSelector 
                (cfg.__ducks) 
                (cfg.__halFormsTemplate.actions[0].id) 
                ('isInProgress'));

    const [openImageDialog, setOpenImageDialog] = useState(false);
    const handleOpenImageDialog = () => setOpenImageDialog(true);
    const handleCloseImageDialog = () => setOpenImageDialog(false);

    const [openInventoryDialog, setOpenInventoryDialog] = useState(false);
    const handleOpenInventoryDialog = () => setOpenInventoryDialog(true);
    const handleCloseInventoryDialog = () => setOpenInventoryDialog(false);

    const row = (props.row) ? props.row : {};

    const stock = (row && row.stock) ? row.stock : {}
    const sku = (stock.sku) ? stock.sku : (stock.barcode) ? stock.barcode : '';
    const name = (stock.name && stock.name.bg) ? stock.name.bg : (stock.name.en) ? stock.name.en : '';

    // PATCH: 
    const imageUrl = `https://trix.tendenz.bg/rp/${sku.substring(0, sku.length - 2)}_1.jpg`;
    // const imageUrl = 'https://img.tendenz.bg/2d0f648eb7b42b1bc5e429268d58b9dd0137269.jpg';

    // price
    const discountPercent = props.row.discountPercent;
    const grossPriceMoney = props.row.grossPrice;
    const grossPriceBeforeDiscountMoney = props.row.grossPriceBeforeDiscount;
    const discountMoney = props.row.discount;

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    // alignItems: "flex-start",
                    alignItems: "stretch",
                    gap: 2,
                    p: "8px",
                    justifyContent: "space-between",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        flex: '0 0 auto',
                        width: 100,
                    }}
                >
                    <Image
                        src={imageUrl}
                        style={{ cursor: 'pointer', borderRadius: 8 }}
                        onClick={handleOpenImageDialog}
                        showLoading
                    />
                </Box>

                <Box 
                    sx={{
                        flex: '1 1 auto',
                        minWidth: '100px',
                        wordBreak: 'break-word',
                    }}
                >
                    <Typography variant='subtitle2' display='block'>
                        {sku}
                    </Typography>
                    <Typography variant='caption' display='block'>
                        {name}
                    </Typography>
                    {/* Warehouse qty indicator */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            In stock: 1 pcs
                        </Typography>
                        <IconButton 
                            size="small" 
                            onClick={handleOpenInventoryDialog}
                        >
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* PRICE BLOCK */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        minWidth: "120px",
                    }}
                >
                    {discountPercent > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocalOfferIcon fontSize="small" color="error" />
                            <Typography
                                variant="body2"
                                sx={{
                                    textDecoration: "line-through",
                                    color: "text.disabled",
                                }}
                            >
                                {fmtMoney(grossPriceBeforeDiscountMoney)}
                            </Typography>
                        </Box>
                    )}

                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: discountPercent > 0 ? "success.main" : "text.primary" }}
                    >
                        {fmtMoney(grossPriceMoney)}
                    </Typography>

                    {discountPercent > 0 && (
                        <Typography variant="caption" color="error.main">
                            -{fmtPercent(discountPercent)} ({fmtMoney(discountMoney)})
                        </Typography>
                    )}

                    {/* Add to sale button */}
                    <Box sx={{ mt: "auto" }}>
                        <IconButton
                            color="primary"
                            size="small"
                            disabled={inProgress}
                            sx={{ 
                                // mt: 1, 
                                // bgcolor: 'primary.light', 
                                // '&:hover': { bgcolor: 'primary.main', color: 'white' } 
                            }}
                            onClick={(event) => {
                                let action = null;
                                for (let n in cfg.__halFormsTemplate.actions) {
                                    if (cfg.__halFormsTemplate.actions[n].name === 'onAddItem') {
                                        action = cfg.__halFormsTemplate.actions[n];
                                        break;
                                    }
                                }

                                if (action) {
                                    cfg.__halFormsActionExtender.executeHalFormsAction(
                                        action, props.row, cfg.container, {});
                                }

                                return;
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {/* Image modal */}
            <Modal open={openImageDialog} onClose={handleCloseImageDialog}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        outline: 'none',
                        boxShadow: 24,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        maxWidth: '90%',
                        maxHeight: '90%',
                        p: 1,
                    }}
                >
                    <Image
                        src={imageUrl}
                        fit='contain'
                        style={{ maxWidth: '100%', maxHeight: '80vh' }}
                    />
                </Box>
            </Modal>

            {/* Dialog with warehouse details */}
            <Dialog open={openInventoryDialog} onClose={handleCloseInventoryDialog} fullWidth maxWidth="sm">
                <DialogTitle>Warehouse Stock Details</DialogTitle>
                <DialogContent>
                    <List>
                        <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Wh NAme</Typography>
                            <Typography variant="body2">5 pcs</Typography>
                        </ListItem>
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
}

const createRetailStockInventoryAndPriceTableColumn = (cfg: any): any => { 
    return {
        flex: true,
        width: 200,

        renderCell: createRetailStockInventoryAndPriceCellRenderer({
            ...cfg.cellRendererFactoryProps,
        })
    }
};
export default createRetailStockInventoryAndPriceTableColumn;
